import { otpService } from "@services/otpService"
import type {
	AgentEmailVerificationRequest,
	AgencyEmailVerificationRequest,
	GenerateOTPResponse
} from "@services/otpService"
import { AxiosError } from "axios"
import { useDebouncedMutations } from "./useDebouncedMutations"

export const useOTPMutations = () => {
	const { createDebouncedMutation } = useDebouncedMutations(5000) // 5 seconds

	const generateOTP = createDebouncedMutation(
		"generateOTP",
		otpService.generateOTP
	)

	const sendRegisterAgentEmailVerification = createDebouncedMutation<
		GenerateOTPResponse,
		AxiosError,
		AgentEmailVerificationRequest
	>(
		"sendRegisterAgentEmailVerification",
		async ({
			email,
			agencyPhoneNumber,
			agencyAbtaNumber,
			languageCode
		}: AgentEmailVerificationRequest) => {
			return otpService.sendRegisterAgentEmailVerification(
				email,
				agencyPhoneNumber,
				agencyAbtaNumber,
				languageCode
			)
		}
	)

	const sendRegisterAgencyEmailVerification = createDebouncedMutation<
		GenerateOTPResponse,
		AxiosError,
		AgencyEmailVerificationRequest
	>(
		"sendRegisterAgencyEmailVerification",
		async ({ email, country }: AgencyEmailVerificationRequest) => {
			return otpService.sendRegisterAgencyEmailVerification(email, country)
		}
	)

	const sendPhoneVerification = createDebouncedMutation<
		GenerateOTPResponse,
		AxiosError,
		{ phone: string; emailToken?: string }
	>("sendPhoneVerification", async ({ phone, emailToken }) => {
		return otpService.sendPhoneVerification(phone, emailToken)
	})

	const sendPhoneVerificationSecured = createDebouncedMutation<
		GenerateOTPResponse,
		AxiosError,
		string
	>("sendPhoneVerificationSecured", async (phone: string) => {
		return otpService.sendPhoneVerificationSecured(phone)
	})

	const sendEmailVerificationOTP = createDebouncedMutation<
		GenerateOTPResponse,
		AxiosError,
		string
	>("sendEmailVerificationOTP", async (email: string) => {
		return otpService.sendEmailVerificationOTP(email)
	})

	const validateOTP = createDebouncedMutation(
		"validateOTP",
		async ({ token, otp }: { token: string; otp?: string }) => {
			return otpService.validateOTP(token, otp)
		}
	)

	return {
		generateOTP,
		validateOTP,
		sendRegisterAgentEmailVerification,
		sendRegisterAgencyEmailVerification,
		sendPhoneVerification,
		sendPhoneVerificationSecured,
		sendEmailVerificationOTP,
		isLoading:
			validateOTP.isPending ||
			sendPhoneVerification.isPending ||
			sendPhoneVerificationSecured.isPending ||
			sendEmailVerificationOTP.isPending
	}
}
